import React, { useEffect, useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ArrowRight, CheckCircle2, Lightbulb } from 'lucide-react';
import { categories, Product } from '../data/products';
// In-context material sanity check.
//
// Asks two quick questions about the customer's project, then surfaces a
// recommended material. The recommendation matrix is CATEGORY-AWARE:
// each top-level product category in `data/products.ts` ships its own
// pair of questions and its own recommendation lookup table so the
// helper actually understands the products the customer is browsing.
//
// Used on PRODUCT detail pages: if the recommendation matches the
// product the customer is viewing, we affirm it; if it doesn't, we
// route them to the right product page instead. Lives here (and not
// on the cart drawer) so the customer makes the decision BEFORE
// adding to cart — when intent is fresh and the swap doesn't require
// a cart-line rewrite.
type Recommendation = {
  productSlug: string;
  productName: string;
  category: string;
  pricePerYard: number;
  why: string;
};
type Option<T extends string> = {
  id: T;
  label: string;
};
type Recipe<P extends string, S extends string> = {
  /** Title for question 1 (e.g. "Project"). */
  step1Label: string;
  /** Title for question 2 (e.g. "Most important"). */
  step2Label: string;
  step1Options: Option<P>[];
  step2Options: Option<S>[];
  /** Returns a `slug` + `why` for the given pair of answers. */
  decide: (p: P, s: S) => {
    slug: string;
    why: string;
  };
};
// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------
// Look up a product (across every category) by its slug. Returns the
// full `Product` plus the category name, or `null` if not found.
function findProductBySlug(slug: string): {
  product: Product;
  category: string;
} | null {
  for (const cat of categories) {
    const p = cat.products.find(pp => pp.slug === slug);
    if (p) return {
      product: p,
      category: cat.name
    };
  }
  return null;
}
// Build a `Recommendation` object from a slug + why. If the slug is
// invalid (typo in a recipe), we return null so the UI doesn't show
// a broken card. (Defensive — we audit the recipes against products
// at write-time so this should never fire in practice.)
function buildRec(slug: string, why: string): Recommendation | null {
  const hit = findProductBySlug(slug);
  if (!hit) return null;
  return {
    productSlug: hit.product.slug,
    productName: hit.product.name,
    category: hit.category,
    pricePerYard: hit.product.pricePerYard,
    why
  };
}
// ----------------------------------------------------------------------
// Recipes — one per product category
// ----------------------------------------------------------------------
// === Base & Compaction ===============================================
// Covers driveway/patio/walkway/parking base rock plus the
// fill / sand / borrow products that live in this category.
type BaseProject = 'driveway' | 'patio' | 'walkway' | 'parking' | 'fill' | 'concrete';
type BasePriority = 'strength' | 'budget' | 'drainage' | 'looks';
const baseRecipe: Recipe<BaseProject, BasePriority> = {
  step1Label: 'Project',
  step2Label: 'Most important',
  step1Options: [{
    id: 'driveway',
    label: 'Driveway'
  }, {
    id: 'patio',
    label: 'Patio base'
  }, {
    id: 'walkway',
    label: 'Walkway'
  }, {
    id: 'parking',
    label: 'Parking pad'
  }, {
    id: 'fill',
    label: 'Fill / grade'
  }, {
    id: 'concrete',
    label: 'Concrete / mortar'
  }],
  step2Options: [{
    id: 'strength',
    label: 'Strong base'
  }, {
    id: 'drainage',
    label: 'Drainage'
  }, {
    id: 'looks',
    label: 'Looks good'
  }, {
    id: 'budget',
    label: 'Best price'
  }],
  decide: (project, priority) => {
    // Concrete / mortar mixing → always sand, regardless of priority.
    if (project === 'concrete') return {
      slug: 'builders-sand',
      why: 'Coarse washed sand — the right material for mixing concrete and mortar.'
    };
    // Fill / grade family — sand/borrow/fill dirt/topsoil cap.
    if (project === 'fill') {
      if (priority === 'budget') return {
        slug: 'fill-dirt',
        why: 'Cheapest bulk material for raising grade and backfilling.'
      };
      // "Looks good" + fill is a misleading combo — the customer still
      // needs a structural fill material for the volume, and topsoil is
      // a finish layer, not a substitute. Recommend the structural fill
      // (type-17 gravel borrow) and call out the topsoil cap in the
      // rationale so the customer understands they may need both.
      if (priority === 'looks') return {
        slug: 'type-17-gravel-borrow',
        why: 'Use this structural fill for the bulk volume — then cap with 3-Way Topsoil on top if you plan to plant or seed over it.'
      };
      return {
        slug: 'type-17-gravel-borrow',
        why: 'Pit-run sand/silt/gravel blend — compacts well for subgrade and structural fill.'
      };
    }
    // Driveway
    if (project === 'driveway') {
      if (priority === 'budget') return {
        slug: 'recycled-concrete',
        why: 'Most affordable driveway base — compacts almost as well as virgin minus.'
      };
      if (priority === 'drainage') return {
        slug: '1-25-minus',
        why: 'Coarser minus with better water shedding than 5/8" while still compacting.'
      };
      return {
        slug: '5-8-minus',
        why: 'Our most popular driveway gravel — packs hard, smooth finish.'
      };
    }
    // Patio base
    if (project === 'patio') {
      if (priority === 'budget') return {
        slug: 'recycled-concrete',
        why: 'Affordable structural base for patios and pavers.'
      };
      if (priority === 'drainage') return {
        slug: '5-8-clear',
        why: 'Clean rock under pavers lets water move through instead of pooling.'
      };
      if (priority === 'strength') return {
        slug: '1-25-minus',
        why: 'Heavy-duty structural base — use under pavers and slabs that take real load.'
      };
      return {
        slug: '5-8-minus',
        why: 'Smooth, well-graded base that locks up tight under patios and pavers.'
      };
    }
    // Walkway
    if (project === 'walkway') {
      if (priority === 'budget') return {
        slug: 'recycled-concrete',
        why: 'Most affordable walkway base that still compacts.'
      };
      return {
        slug: '3-8-minus-driveway',
        why: 'Finer 3/8" minus — smoother surface and easier on bare feet than 5/8".'
      };
    }
    // Parking pad
    if (project === 'parking') {
      if (priority === 'budget') return {
        slug: 'recycled-asphalt',
        why: 'Affordable and binds tighter than crushed rock under tires and weight.'
      };
      return {
        slug: '1-25-minus',
        why: 'Heavy-duty base for parking pads and vehicle storage areas.'
      };
    }
    return {
      slug: '5-8-minus',
      why: 'Our most popular base — works for most driveway, patio and walkway jobs.'
    };
  }
};
// === Clear & Drain ===================================================
// Washed clean rock for drainage applications + pea gravel.
type DrainProject = 'french-drain' | 'retaining-wall' | 'septic' | 'pathway';
type DrainFlow = 'light' | 'moderate' | 'heavy';
const drainRecipe: Recipe<DrainProject, DrainFlow> = {
  step1Label: 'Drainage use',
  step2Label: 'Water flow',
  step1Options: [{
    id: 'french-drain',
    label: 'French drain'
  }, {
    id: 'retaining-wall',
    label: 'Wall backfill'
  }, {
    id: 'septic',
    label: 'Septic / curtain'
  }, {
    id: 'pathway',
    label: 'Pathway / decor'
  }],
  step2Options: [{
    id: 'light',
    label: 'Light'
  }, {
    id: 'moderate',
    label: 'Moderate'
  }, {
    id: 'heavy',
    label: 'Heavy'
  }],
  decide: (project, flow) => {
    if (project === 'french-drain') {
      if (flow === 'light') return {
        slug: '5-8-clear',
        why: 'Small clean rock — fine for short residential drains with light flow.'
      };
      if (flow === 'heavy') return {
        slug: '1-5-drain-rock',
        why: 'Largest washed drain rock — moves serious water during heavy PNW storms.'
      };
      return {
        slug: '7-8-drain-rock',
        why: 'Standard PNW residential French drain spec — washed, rounded, fast-flowing.'
      };
    }
    if (project === 'retaining-wall') {
      if (flow === 'light') return {
        slug: '3-4-clean-rock',
        why: 'Mid-size clean rock — easy to place behind a wall and still drains fast.'
      };
      if (flow === 'heavy') return {
        slug: '1-5-drain-rock',
        why: 'Open structure moves heavy hydrostatic pressure away from the wall.'
      };
      return {
        slug: '1-25-clear',
        why: 'Standard retaining-wall backfill — large voids drain water away from the wall.'
      };
    }
    if (project === 'septic') {
      if (flow === 'light') return {
        slug: '7-8-drain-rock',
        why: 'Standard drain rock works for smaller drainfields.'
      };
      if (flow === 'heavy') return {
        slug: '1-5-drain-rock',
        why: 'Largest drain rock — best for large fields and commercial-scale drainage.'
      };
      return {
        slug: '1-25-clear',
        why: 'Big voids move water fast — the workhorse for septic fields and curtain drains.'
      };
    }
    // pathway
    if (flow === 'heavy') return {
      slug: '3-4-clean-rock',
      why: 'Decorative pathway clean rock that still drains hard — easy to rake.'
    };
    if (flow === 'moderate') return {
      slug: '5-8-clear',
      why: 'Clean, uniform 5/8" rock — good for visible drainage and pathway surfaces.'
    };
    return {
      slug: 'pea-gravel',
      why: 'Smooth, rounded ~3/8" stone — comfortable underfoot for pathways and dog runs.'
    };
  }
};
// === Soil, Mulch & Org. ==============================================
type SoilProject = 'new-lawn' | 'planting-bed' | 'topdress' | 'mulch-beds' | 'playground';
type SoilTexture = 'fine' | 'medium' | 'coarse';
const soilRecipe: Recipe<SoilProject, SoilTexture> = {
  step1Label: 'Project',
  step2Label: 'Texture / look',
  step1Options: [{
    id: 'new-lawn',
    label: 'New lawn'
  }, {
    id: 'planting-bed',
    label: 'Planting bed'
  }, {
    id: 'topdress',
    label: 'Top-dress'
  }, {
    id: 'mulch-beds',
    label: 'Mulch beds'
  }, {
    id: 'playground',
    label: 'Playground'
  }],
  step2Options: [{
    id: 'fine',
    label: 'Fine'
  }, {
    id: 'medium',
    label: 'Medium'
  }, {
    id: 'coarse',
    label: 'Coarse'
  }],
  decide: (project, texture) => {
    if (project === 'playground') return {
      slug: 'play-chips',
      why: 'Certified play chips — soft, safe cushion under play structures.'
    };
    if (project === 'mulch-beds') {
      if (texture === 'fine') return {
        slug: 'fine-bark',
        why: 'Finest grade bark — premium finished look on beds and around plants.'
      };
      if (texture === 'coarse') return {
        slug: 'coarse-bark',
        why: 'Chunky coarse bark — long-lasting cover that resists blowing and washing.'
      };
      return {
        slug: 'medium-bark',
        why: 'Most popular bark — balances looks and longevity in landscape beds.'
      };
    }
    if (project === 'topdress') {
      if (texture === 'fine') return {
        slug: 'compost',
        why: 'Pure compost — perfect for top-dressing lawns and feeding planting beds.'
      };
      return {
        slug: '3-way-topsoil',
        why: 'Balanced topsoil blend — great for top-dressing thin lawns and grading.'
      };
    }
    if (project === 'planting-bed') {
      if (texture === 'fine') return {
        slug: 'compost',
        why: 'Compost amends soil structure and adds nutrients to planting beds.'
      };
      if (texture === 'coarse') return {
        slug: '50-50-mix',
        why: 'Half topsoil / half compost — boosts existing beds before planting.'
      };
      return {
        slug: '3-way-topsoil',
        why: 'Workhorse topsoil for filling and planting raised beds.'
      };
    }
    // new-lawn
    if (texture === 'fine') return {
      slug: '3-way-topsoil',
      why: 'Smooth, screened topsoil — best surface to seed or sod a new lawn over.'
    };
    return {
      slug: '50-50-mix',
      why: 'Topsoil blended with compost — gives new turf a fertile head start.'
    };
  }
};
// === Quarry Rock — rip-rap, erosion, structural armoring =============
type QuarryUse = 'rip-rap' | 'wall-armor' | 'construction-access' | 'decorative-boulder';
type QuarrySeverity = 'mild' | 'steep' | 'extreme';
const quarryRecipe: Recipe<QuarryUse, QuarrySeverity> = {
  step1Label: 'Use',
  step2Label: 'Slope / severity',
  step1Options: [{
    id: 'rip-rap',
    label: 'Rip-rap / erosion'
  }, {
    id: 'wall-armor',
    label: 'Wall armor'
  }, {
    id: 'construction-access',
    label: 'Site access'
  }, {
    id: 'decorative-boulder',
    label: 'Boulders'
  }],
  step2Options: [{
    id: 'mild',
    label: 'Mild'
  }, {
    id: 'steep',
    label: 'Steep'
  }, {
    id: 'extreme',
    label: 'Extreme'
  }],
  decide: (use, severity) => {
    if (use === 'decorative-boulder') {
      if (severity === 'mild') return {
        slug: '2-4-quarry-rock',
        why: '2–4" angular spalls — small accent rock for borders and beds.'
      };
      if (severity === 'steep') return {
        slug: '4-8-quarry-spalls',
        why: '4–8" spalls — mid-size landscape boulders with real visual weight.'
      };
      return {
        slug: 'large-rocks',
        why: '1-Man to 4-Man boulders — the statement pieces of any landscape.'
      };
    }
    if (use === 'construction-access') {
      if (severity === 'extreme') return {
        slug: '4-8-quarry-spalls',
        why: 'Bridges through deep mud and stabilizes severely soft ground.'
      };
      if (severity === 'steep') return {
        slug: '2-4-quarry-rock',
        why: 'Locks together for temporary construction entrances and haul roads.'
      };
      return {
        slug: '2-inch-ballast',
        why: 'Uniform 2" angular ballast — stable footing and good drainage for site access.'
      };
    }
    if (use === 'wall-armor') {
      if (severity === 'mild') return {
        slug: '2-inch-ballast',
        why: 'Clean uniform 2" — solid drainage layer behind small structural walls.'
      };
      if (severity === 'extreme') return {
        slug: 'large-rocks',
        why: 'Massive 1-Man to 4-Man rocks — the only thing that holds against extreme load.'
      };
      return {
        slug: '4-8-quarry-spalls',
        why: 'Heavy spalls — interlocking armor behind large retaining walls.'
      };
    }
    // rip-rap (erosion)
    if (severity === 'mild') return {
      slug: '2-4-quarry-rock',
      why: 'Standard rip-rap size for ditches and gentle bank stabilization.'
    };
    if (severity === 'extreme') return {
      slug: 'large-rocks',
      why: 'Shoreline-grade armor — nothing displaces 1-Man to 4-Man rocks.'
    };
    return {
      slug: '4-8-quarry-spalls',
      why: 'Heavy rip-rap for steep banks and shoreline exposure.'
    };
  }
};
// === Landscape & Decor ===============================================
type DecorUse = 'dry-creek' | 'water-feature' | 'garden-accent' | 'pathway-cover';
type DecorLook = 'natural-mixed' | 'smooth-rounded' | 'light-volcanic';
const decorRecipe: Recipe<DecorUse, DecorLook> = {
  step1Label: 'Use',
  step2Label: 'Look',
  step1Options: [{
    id: 'dry-creek',
    label: 'Dry creek bed'
  }, {
    id: 'water-feature',
    label: 'Water feature'
  }, {
    id: 'garden-accent',
    label: 'Garden accent'
  }, {
    id: 'pathway-cover',
    label: 'Ground cover'
  }],
  step2Options: [{
    id: 'natural-mixed',
    label: 'Natural mixed'
  }, {
    id: 'smooth-rounded',
    label: 'Smooth rounded'
  }, {
    id: 'light-volcanic',
    label: 'Light / volcanic'
  }],
  decide: (_use, look) => {
    if (look === 'smooth-rounded') return {
      slug: 'river-rock',
      why: 'Tumbled, rounded river stones — clean, polished look for any setting.'
    };
    if (look === 'light-volcanic') return {
      slug: 'lava-rock',
      why: 'Lightweight red/black volcanic rock — easy to handle, dramatic contrast.'
    };
    return {
      slug: 'streambed-cobble',
      why: 'Mixed-size natural cobble — the most authentic, rugged streambed look.'
    };
  }
};
// ----------------------------------------------------------------------
// Recipe dispatch by category
// ----------------------------------------------------------------------
// Returns the recipe that matches the contextProduct's category, plus
// the resolved category name. Returns null for any category that isn't
// wired up here (the helper hides itself in that case rather than
// showing a misleading recommendation).
function getRecipeForSlug(slug: string): {
  category: string;
  recipe: Recipe<string, string>;
} | null {
  const hit = findProductBySlug(slug);
  if (!hit) return null;
  switch (hit.category) {
    case 'Base & Compaction':
      return {
        category: hit.category,
        recipe: baseRecipe as Recipe<string, string>
      };
    case 'Clear & Drain':
      return {
        category: hit.category,
        recipe: drainRecipe as Recipe<string, string>
      };
    case 'Soil, Mulch & Org.':
      return {
        category: hit.category,
        recipe: soilRecipe as Recipe<string, string>
      };
    case 'Quarry Rock':
      return {
        category: hit.category,
        recipe: quarryRecipe as Recipe<string, string>
      };
    case 'Landscape & Decor':
      return {
        category: hit.category,
        recipe: decorRecipe as Recipe<string, string>
      };
    default:
      return null;
  }
}
// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
type ContextProduct = {
  slug: string;
  name: string;
};
type Props = {
  /** The product the user is currently viewing. The widget compares the
   *  recommendation against this product. */
  contextProduct: ContextProduct;
  className?: string;
};
export function CartHelper({
  contextProduct,
  className = ''
}: Props) {
  const [open, setOpen] = useState(false);
  const [step1, setStep1] = useState<string | null>(null);
  const [step2, setStep2] = useState<string | null>(null);
  // Resolve the right recipe for this product's category. If we don't
  // have a recipe (unsupported category), render nothing — better than
  // showing a helper that will always recommend the wrong thing.
  const resolved = useMemo(() => getRecipeForSlug(contextProduct.slug), [contextProduct.slug]);
  // Relevance guard: enumerate every (step1, step2) combination in the
  // active recipe and check whether ANY combo would recommend the
  // product the customer is currently viewing. If NONE do, the helper
  // can never affirm the page — it would only ever route the customer
  // elsewhere — so it's noise. Hide it. This protects against future
  // products being added to a category without a matching path in the
  // recipe; the helper auto-quiets itself instead of misleading users.
  const canAffirmContext = useMemo(() => {
    if (!resolved) return false;
    for (const s1 of resolved.recipe.step1Options) {
      for (const s2 of resolved.recipe.step2Options) {
        const decision = resolved.recipe.decide(s1.id, s2.id);
        if (decision.slug === contextProduct.slug) return true;
      }
    }
    return false;
  }, [resolved, contextProduct.slug]);
  // Reset the two answers when the product changes, so going from one
  // product page to another doesn't keep stale selections in state.
  useEffect(() => {
    setStep1(null);
    setStep2(null);
  }, [contextProduct.slug]);
  const rec = useMemo<Recommendation | null>(() => {
    if (!resolved || !step1 || !step2) return null;
    const decision = resolved.recipe.decide(step1, step2);
    return buildRec(decision.slug, decision.why);
  }, [resolved, step1, step2]);
  const matchesContext = useMemo(() => {
    if (!rec) return false;
    return rec.productSlug === contextProduct.slug;
  }, [rec, contextProduct.slug]);
  if (!resolved || !canAffirmContext) return null;
  return <div className={`rounded-xl bg-[#f4f7f2] dark:bg-emerald-200/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 ${className}`} data-id="element-647" data-mp="thqtvjek565c">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" aria-expanded={open} data-id="element-648" data-mp="kgj820t89xs5">
        <div className="flex items-center gap-2.5 min-w-0" data-id="element-649" data-mp="3a3i9llpxrh4z">
          <div className="w-8 h-8 rounded-full bg-[#5d9d4e]/15 flex items-center justify-center shrink-0" data-id="element-650" data-mp="355gqj0sifhti">
            <Lightbulb className="w-4 h-4 text-[#3d7530] dark:text-[#9dd194]" data-id="element-651" data-mp="1kpr1fb12n3f0" />
          </div>
          <div className="min-w-0" data-id="element-652" data-mp="1pols0z5a5w9o">
            <p className="text-sm font-bold text-stone-900 dark:text-white truncate" data-id="element-653" data-mp="1plg7lx7ljika">
              Not sure {contextProduct.name} is right?
            </p>
            <p className="text-[11px] text-stone-600 dark:text-stone-300 truncate" data-id="element-654" data-mp="1ygw4yo7k8fum">
              Answer 2 quick questions — we'll suggest the best match.
            </p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-stone-500 dark:text-stone-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-655" data-mp="28cz8nyi28mjo" />
      </button>

      <AnimatePresence initial={false} data-id="element-656" data-mp="2dbadtvxeu8vk">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-657" data-mp="2hdf8mxzlhbjs">
            <div className="px-4 pb-4 pt-1 space-y-4 border-t border-[#dde7d6] dark:border-emerald-500/20" data-id="element-658" data-mp="1qb38v0x7rjv6">
              {/* Step 1 */}
              <div data-id="element-659" data-mp="1le32sq0d6h42">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-600 dark:text-stone-300 mb-2" data-id="element-660" data-mp="1lemmoc9krfkq">
                  {resolved.recipe.step1Label}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-661" data-mp="1v9egd501dia8">
                  {resolved.recipe.step1Options.map(opt => {
                const active = step1 === opt.id;
                return <button key={opt.id} type="button" onClick={() => setStep1(opt.id)} className={`w-full px-2 py-2.5 rounded-lg text-xs font-bold transition-colors border ${active ? 'bg-[#5d9d4e] text-white border-[#5d9d4e]' : 'bg-[#fffff9] dark:bg-white/[0.04] border-[#dde7d6] dark:border-white/10 text-stone-700 dark:text-stone-300 hover:border-[#5d9d4e]'}`} data-id="element-662" data-mp="24ausbh6i1sgk">
                        {opt.label}
                      </button>;
              })}
                </div>
              </div>

              {/* Step 2 */}
              <div data-id="element-663" data-mp="36lksy6vlojsc">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-600 dark:text-stone-300 mb-2" data-id="element-664" data-mp="3bmqmg6x34b2x">
                  {resolved.recipe.step2Label}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-665" data-mp="2xpbtwtbjbn6q">
                  {resolved.recipe.step2Options.map(opt => {
                const active = step2 === opt.id;
                return <button key={opt.id} type="button" onClick={() => setStep2(opt.id)} className={`w-full px-2 py-2.5 rounded-lg text-xs font-bold transition-colors border ${active ? 'bg-[#5d9d4e] text-white border-[#5d9d4e]' : 'bg-[#fffff9] dark:bg-white/[0.04] border-[#dde7d6] dark:border-white/10 text-stone-700 dark:text-stone-300 hover:border-[#5d9d4e]'}`} data-id="element-666" data-mp="2nv33qc9z86bq">
                        {opt.label}
                      </button>;
              })}
                </div>
              </div>

              {/* Result */}
              <AnimatePresence data-id="element-667" data-mp="21wpcbxoik5t9">
                {rec && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25
            }}
            // Sits flat on the parent's form surface — no
            // inner card background or border. Keeps padding so
            // the content has breathing room but lets the
            // outer darker green show through.
            className="p-4" data-id="element-668" data-mp="21vnn8panxisj">
                    {matchesContext ? <div className="flex items-start gap-3" data-id="element-669" data-mp="1981llmkzwo8b">
                        <CheckCircle2 className="w-5 h-5 text-brand-button shrink-0 mt-0.5" data-id="element-670" data-mp="198jov362s2pq" />
                        <div className="min-w-0" data-id="element-671" data-mp="1580vv5sg4qly">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-672" data-mp="33lssiebxwu02">
                            You're on the right page.
                          </p>
                          <p className="text-[11px] text-stone-600 dark:text-stone-300 mt-0.5" data-id="element-673" data-mp="2zid05u6wlft6">
                            {rec.why}
                          </p>
                        </div>
                      </div> : <div className="space-y-3" data-id="element-674" data-mp="q9opfv118ox0">
                        <div data-id="element-675" data-mp="v3khkgum7hk9">
                          <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 mb-1" data-id="element-676" data-mp="v3b0xjvgmmr8">
                            We'd recommend
                          </p>
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-677" data-mp="2fomfgkp1xpl">
                            {rec.productName}{' '}
                            <span className="font-normal text-stone-500 dark:text-stone-400" data-id="element-678" data-mp="be0msabd49v8">
                              · ${rec.pricePerYard}/yd
                            </span>
                          </p>
                          <p className="text-[11px] text-stone-600 dark:text-stone-300 mt-1" data-id="element-679" data-mp="1mugsvd111e5f">
                            {rec.why}
                          </p>
                        </div>
                        <Link to={`/shop/${rec.productSlug}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-button hover:bg-brand-buttonHover text-white font-bold text-xs transition-colors" data-id="element-680" data-mp="3nvdlkdao8swb">
                          View {rec.productName}
                          <ArrowRight className="w-3.5 h-3.5" data-id="element-681" data-mp="1mce89032pym" />
                        </Link>
                      </div>}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}